// src/pages/products/components/OffersModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Trash2, Plus, Tag, AlertCircle } from 'lucide-react';
import styles from './OffersModal.module.css';

const EMPTY_OFFER = {
  quantity: '',
  criteria: 'equal',
  unitPrice: '',
  totalAmount: '',
  priceMode: 'unit', // 'unit' | 'total' — tracks which field was last edited
  overrideEnabled: false,
  overrideDeliveryCharge: '',
};

const OffersModal = ({ isOpen, onClose, onSave, offers = [], productName }) => {
  const [localOffers, setLocalOffers] = useState([]);
  const [newOffer, setNewOffer] = useState({ ...EMPTY_OFFER });
  const [addError, setAddError] = useState('');
  const [applyAsDiscount, setApplyAsDiscount] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalOffers(offers.map(o => ({ ...o })));
      setNewOffer({ ...EMPTY_OFFER });
      setAddError('');
      setApplyAsDiscount(!!offers[0]?.applyAsDiscount);
    }
  }, [isOpen, offers]);

  if (!isOpen) return null;

  const handleNewOfferChange = (field, value) => {
    setNewOffer(prev => ({ ...prev, [field]: value }));
    setAddError('');
  };

  // Separate handlers for the linked price fields so each can update the other.
  const handleQuantityChange = (value) => {
    setNewOffer(prev => {
      const qty = parseInt(value, 10);
      if (isNaN(qty) || qty < 1) return { ...prev, quantity: value };
      if (prev.priceMode === 'total' && prev.totalAmount !== '') {
        const total = parseFloat(prev.totalAmount);
        if (!isNaN(total) && total >= 0) {
          return { ...prev, quantity: value, unitPrice: (total / qty).toFixed(4) };
        }
      } else if (prev.unitPrice !== '') {
        const price = parseFloat(prev.unitPrice);
        if (!isNaN(price) && price >= 0) {
          return { ...prev, quantity: value, totalAmount: (price * qty).toFixed(2) };
        }
      }
      return { ...prev, quantity: value };
    });
    setAddError('');
  };

  const handleUnitPriceChange = (value) => {
    setNewOffer(prev => {
      const qty = parseInt(prev.quantity, 10);
      const price = parseFloat(value);
      const totalAmount = (!isNaN(qty) && qty > 0 && !isNaN(price) && price >= 0)
        ? (price * qty).toFixed(2)
        : '';
      return { ...prev, unitPrice: value, totalAmount, priceMode: 'unit' };
    });
    setAddError('');
  };

  const handleTotalAmountChange = (value) => {
    setNewOffer(prev => {
      const qty = parseInt(prev.quantity, 10);
      const total = parseFloat(value);
      // Show 4 d.p. in the unit price field for readability; the full-precision
      // float is recomputed from totalAmount at save time (see handleAddOffer).
      const unitPrice = (!isNaN(qty) && qty > 0 && !isNaN(total) && total >= 0)
        ? (total / qty).toFixed(4)
        : '';
      return { ...prev, totalAmount: value, unitPrice, priceMode: 'total' };
    });
    setAddError('');
  };

  const handleAddOffer = () => {
    const qty = parseInt(newOffer.quantity, 10);

    if (!newOffer.quantity || isNaN(qty) || qty < 1) {
      setAddError('Quantity must be a positive integer.');
      return;
    }

    // When totalAmount is the source, compute unitPrice at full float precision
    // (no toFixed) so that qty × unitPrice rounds back to the exact total.
    let price;
    if (newOffer.priceMode === 'total' && newOffer.totalAmount !== '') {
      const total = parseFloat(newOffer.totalAmount);
      if (isNaN(total) || total < 0) {
        setAddError('Total amount must be a valid non-negative number.');
        return;
      }
      price = total / qty; // full float precision — no rounding loss
    } else {
      price = parseFloat(newOffer.unitPrice);
      if (!newOffer.unitPrice || isNaN(price) || price < 0) {
        setAddError('Unit price must be a valid non-negative number.');
        return;
      }
    }

    const offer = {
      quantity: qty,
      criteria: newOffer.criteria,
      unitPrice: price,
      overrideDeliveryCharge:
        newOffer.overrideEnabled && newOffer.overrideDeliveryCharge !== ''
          ? parseFloat(newOffer.overrideDeliveryCharge)
          : null,
      applyAsDiscount,
    };

    setLocalOffers(prev => [...prev, offer]);
    setNewOffer({ ...EMPTY_OFFER });
    setAddError('');
  };

  const handleDeleteOffer = (index) => {
    setLocalOffers(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    // applyAsDiscount is one setting per product-shop, so every offer in the
    // list must carry the same current value of the toggle.
    onSave(localOffers.map(o => ({ ...o, applyAsDiscount })));
    onClose();
  };

  const criteriaLabel = (criteria) =>
    criteria === 'gte' ? '≥' : '=';

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <Tag size={18} className={styles.titleIcon} />
            <div>
              <h3>Quantity-Based Offers</h3>
              {productName && <p className={styles.productName}>{productName}</p>}
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.discountModeRow}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={applyAsDiscount}
                onChange={e => setApplyAsDiscount(e.target.checked)}
              />
              <span>Apply as discount</span>
            </label>
            <p className={styles.discountModeHint}>
              When checked, a matched offer keeps the unit price unchanged and applies the
              saving as a line discount instead.
            </p>
          </div>

          {/* Existing offers list */}
          {localOffers.length > 0 ? (
            <div className={styles.offersList}>
              <div className={styles.offersListHeader}>
                <span>Qty</span>
                <span>Condition</span>
                <span>Unit Price</span>
                <span>Delivery</span>
                <span></span>
              </div>
              {localOffers.map((offer, index) => (
                <div key={index} className={styles.offerRow}>
                  <span className={styles.offerQty}>{offer.quantity}</span>
                  <span className={styles.offerCriteria}>
                    {offer.criteria === 'gte' ? 'Greater than or equal' : 'Equal to'}
                  </span>
                  <span className={styles.offerPrice}>
                    {parseFloat(offer.unitPrice).toFixed(2)}
                  </span>
                  <span className={styles.offerDelivery}>
                    {offer.overrideDeliveryCharge != null
                      ? parseFloat(offer.overrideDeliveryCharge).toFixed(2)
                      : <span className={styles.defaultLabel}>Default</span>}
                  </span>
                  <button
                    type="button"
                    className={styles.deleteOfferBtn}
                    onClick={() => handleDeleteOffer(index)}
                    title="Remove offer"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.noOffers}>No offers configured yet. Add one below.</p>
          )}

          {/* Add offer form */}
          <div className={styles.addOfferSection}>
            <h4 className={styles.addOfferTitle}>Add Offer</h4>

            {addError && (
              <div className={styles.addError}>
                <AlertCircle size={14} />
                {addError}
              </div>
            )}

            <div className={styles.addOfferFields}>
              <div className={styles.fieldGroup}>
                <label>Quantity</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={newOffer.quantity}
                  onChange={e => handleQuantityChange(e.target.value)}
                  placeholder="e.g. 2"
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Condition</label>
                <select
                  value={newOffer.criteria}
                  onChange={e => handleNewOfferChange('criteria', e.target.value)}
                  className={styles.select}
                >
                  <option value="equal">Equal to</option>
                  <option value="gte">Greater than or equal</option>
                </select>
              </div>

              <div className={styles.fieldGroup}>
                <label>Unit Price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newOffer.unitPrice}
                  onChange={e => handleUnitPriceChange(e.target.value)}
                  placeholder="0.00"
                  className={`${styles.input} ${newOffer.priceMode === 'total' ? styles.inputComputed : ''}`}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>
                  Total Amount
                  <span className={styles.fieldHint}> (qty × unit)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newOffer.totalAmount}
                  onChange={e => handleTotalAmountChange(e.target.value)}
                  placeholder="0.00"
                  className={`${styles.input} ${newOffer.priceMode === 'unit' ? styles.inputComputed : ''}`}
                />
              </div>
            </div>

            <div className={styles.overrideRow}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={newOffer.overrideEnabled}
                  onChange={e => {
                    handleNewOfferChange('overrideEnabled', e.target.checked);
                    if (!e.target.checked) handleNewOfferChange('overrideDeliveryCharge', '');
                  }}
                />
                <span>Override Delivery Charge</span>
              </label>
              {newOffer.overrideEnabled && (
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newOffer.overrideDeliveryCharge}
                  onChange={e => handleNewOfferChange('overrideDeliveryCharge', e.target.value)}
                  placeholder="0.00"
                  className={`${styles.input} ${styles.deliveryInput}`}
                />
              )}
            </div>

            <button
              type="button"
              className={styles.addButton}
              onClick={handleAddOffer}
            >
              <Plus size={14} />
              Add Offer
            </button>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button type="button" className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={styles.saveButton} onClick={handleSave}>
            Save Offers
            {localOffers.length > 0 && (
              <span className={styles.saveCount}>{localOffers.length}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OffersModal;
